import { TemplateResult, html, LitElement } from 'lit';
import globalStyles from './styles/outline-nav-link.global.css?inline';
import encapsulatedStyles from './styles/outline-nav-link.encapsulated.css?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';
import { ifDefined } from 'lit/directives/if-defined.js';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import type { MenuNavItem } from '../types';
import { getCurrentPath, _dispatchActiveEvent } from '../utility';
import {
  screenSizeMap,
  ScreenSizeController,
} from '../../../controllers/screen-size-controller';

/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-outline-nav-link';

/**
 * The OutlineNavLink component
 * @element outline-nav-link
 */
@customElement('outline-nav-link')
export class OutlineNavLink extends LitElement {
  resizeObserver: ResizeObserver;

  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
    adoptedStyleSheets = new AdoptedStyleSheets(this, {
      globalCSS: globalStyles,
      encapsulatedCSS: encapsulatedStyles,
    });
  screens = new ScreenSizeController(this);

  /**
   * Item data
   */
  @property({ type: Object, attribute: 'item' })
  item: MenuNavItem;

  /**
   * Attribute used to determine if the item is a top level parent item.
   */
  @property({ type: Boolean })
  toplevel: boolean;

  /**
   * ID of parent element.
   */
  @property({ type: String, reflect: false, attribute: 'parent-id' })
  parentID: string;

  /**
   * If screen is below 768px width.
   */
  @property({ type: Boolean })
  isMobile: boolean;

  /**
   * If Link is a secondary menu level link
   */
  @property({ type: Boolean })
  highlighted: boolean;

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.resetAtScreen();
    });    
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }

  firstUpdated() {
    const self = this.shadowRoot?.querySelector('.menu-link, .menu-link--main') as HTMLElement;
    this.resizeObserver.observe(self);
  }

  resetAtScreen() {
    this.isMobile = window.innerWidth < (screenSizeMap.get('xl') as number);
  }


  render(): TemplateResult {
    const wrapperClasses = {
      'menu-link': !this.toplevel,
      'menu-link--main': this.toplevel,
      'mobile': this.isMobile,
      'animated': !this.isMobile && !this.toplevel,
    };

    return html`
      <span class="${classMap(wrapperClasses)}">
        <a
          aria-label=${ifDefined(this.item.aria)}
          href=${ifDefined(this.item.url)}
          @keydown=${this.handleKeyDown}
          @focus=${this.applyActiveAttribute}
          @blur=${this.removeActiveAttribute}
          >${this.item.text}</a
        >
      </span>
    `;
  }

  /**
   * Checks if the item.url matches the window path.
   * If true, sets the active attribute
   * and calls this._dispatchActiveEvent() if the component has a parent element.
   */
  checkActive() {
    if (this.item.url && getCurrentPath(this.item.url)) {
      this.setAttribute('active', 'active');
      if (this.item.parentID) {
        _dispatchActiveEvent(this);
      }
    }
  }

  applyActiveAttribute() {
    this.shadowRoot!.children[0].setAttribute('active', 'active');
  }

  removeActiveAttribute() {
    this.shadowRoot!.children[0].removeAttribute('active');
  }

  /**
   * Prevents scrolling if the space bar is pressed on anchor.
   */
  handleKeyDown(e?: KeyboardEvent) {
    if (e?.type === 'keydown' && e.key === ' ') {
      e.preventDefault();
    }
  }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineNavLink;
  }
}

